import {
  Component,
  forwardRef,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Component({
  selector: 'piqet-angular-common-input',
  templateUrl: './common-input.component.html',
  styleUrls: ['./common-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CommonInputComponent),
      multi: true,
    },
  ],
})
export class CommonInputComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() label = '';
  @Input() name = '';
  @Input() placeholder = '';
  @Input() maxLength = Infinity;
  @Input() inputType = 'text';
  @Input() control: AbstractControl | null = null;
  @Input() submitted = false;
  @Input() errorMessages: { [key: string]: string } = {};
  @Input() required = false;
  @Input() disabled = false;
  @Input() minValue?: number;
  @Input() maxValue?: number;
  @Input() isPercentage = false;
  @Input() enableRangeValidation = false;
  @Input() preventNegative = false;
  @Input() showPasswordToggle = false;
  @Input() preventDot = false;
  @Output() customBlur = new EventEmitter<Event>();
  @Output() keypressEvent = new EventEmitter<KeyboardEvent>();
  @Output() inputEvent = new EventEmitter<Event>();
  @Output() keyupEvent = new EventEmitter<Event>();

  @Input() showTooltip = false;
  @Input() tooltipText = '';
  @Input() tooltipPlacement: 'auto' | 'top' | 'bottom' | 'left' | 'right' = 'auto';
  @Input() tooltipClass = 'sku-tooltip';
  @Input() tooltipIcon = './assets/icons/grey-question-icon.svg';
  @Input() tooltipIconWidth = 14;
  @Input() tooltipIconHeight = 14;

  @Input() tooltipConfig?: {
    text: string;
    placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right';
    class?: string;
    icon?: string;
    iconWidth?: number;
    iconHeight?: number;
  };

  showPassword = false;
  value = '';

  @Input() decimalPlaces?: number;

  // Track internal disabled state from ControlValueAccessor
  private _internalDisabled = false;

  defaultErrorMessages: { [key: string]: string } = {};

  onChange = (value: string) => {};
  onTouched = () => {};

  ngOnInit() {
    this.defaultErrorMessages = {
      required: `${this.label} is required`,
      whitespace: `${this.label} is required`,
      maxlength: `${this.label} cannot exceed ${this.maxLength} characters`,
      email: 'Please enter a valid email address',
      pattern: `${this.label} format is invalid`,
      mustMatch: 'The password and confirm password fields do not match',
      min: `${this.label} must be at least ${this.minValue ?? 0}${this.isPercentage ? '%' : ''}`,
      max: `${this.label} must not exceed ${this.maxValue}${this.isPercentage ? '%' : ''}`,
      range: `${this.label} must be between ${this.minValue ?? 0}${
        this.isPercentage ? '%' : ''
      } and ${this.maxValue}${this.isPercentage ? '%' : ''}`,
      decimalPlaces: `${this.label} can have maximum ${this.decimalPlaces} decimal places`,
      greaterThanZero: `${this.label} must be greater than 0`,
    };
    if (this.tooltipConfig) {
      this.tooltipText = this.tooltipConfig.text ?? this.tooltipText;
      this.tooltipPlacement = this.tooltipConfig.placement ?? this.tooltipPlacement;
      this.tooltipClass = this.tooltipConfig.class ?? this.tooltipClass;
      this.tooltipIcon = this.tooltipConfig.icon ?? this.tooltipIcon;
      this.tooltipIconWidth = this.tooltipConfig.iconWidth ?? this.tooltipIconWidth;
      this.tooltipIconHeight = this.tooltipConfig.iconHeight ?? this.tooltipIconHeight;
      this.showTooltip = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle disabled state changes
    if (changes['disabled'] && !changes['disabled'].firstChange) {
      // If disabled state changed from outside, update internal state
      this._internalDisabled = this.disabled;
    }

    // Handle value changes if needed
    if (changes['control'] && this.control) {
      // Subscribe to value changes if control changed
      this.value = this.control.value ?? '';
    }

    // Update error messages if label changes
    if (
      changes['label'] ||
      changes['minValue'] ||
      changes['maxValue'] ||
      changes['decimalPlaces']
    ) {
      this.updateDefaultErrorMessages();
    }
  }

  private updateDefaultErrorMessages(): void {
    this.defaultErrorMessages = {
      required: `${this.label} is required`,
      whitespace: `${this.label} is required`,
      maxlength: `${this.label} cannot exceed ${this.maxLength} characters`,
      email: 'Please enter a valid email address',
      pattern: `${this.label} format is invalid`,
      min: `${this.label} must be at least ${this.minValue ?? 0}${this.isPercentage ? '%' : ''}`,
      max: `${this.label} must not exceed ${this.maxValue ?? 100}${this.isPercentage ? '%' : ''}`,
      range: `${this.label} must be between ${this.minValue ?? 0}${
        this.isPercentage ? '%' : ''
      } and ${this.maxValue ?? 100}${this.isPercentage ? '%' : ''}`,
      decimalPlaces: `${this.label} can have maximum ${this.decimalPlaces} decimal places`,
    };
  }

  // Getter to determine if input should be disabled
  get isDisabled(): boolean {
    return this.disabled || this._internalDisabled;
  }

  writeValue(value: string): void {
    if (this.inputType === 'number' && !value) {
      this.value = '';
      return;
    }

    this.value = value?.toString() ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._internalDisabled = isDisabled;
    // Don't update the @Input() disabled here to avoid conflicts
  }

  onKeyPress(event: KeyboardEvent): void {
    // Handle decimal places restriction for number inputs
    if (this.inputType === 'number' && this.decimalPlaces !== undefined) {
      const target = event.target as HTMLInputElement;
      const currentValue = target.value;
      const key = event.key;

      // Allow control keys (backspace, delete, arrow keys, etc.)
      if (this.isControlKey(event)) {
        this.keypressEvent.emit(event);
        return;
      }

      // If user is trying to enter a dot and decimalPlaces is 0, prevent it
      if (key === '.' && this.decimalPlaces === 0) {
        event.preventDefault();
        return;
      }

      // If there's already a decimal point and user enters another dot, prevent it
      if (key === '.' && currentValue.includes('.')) {
        event.preventDefault();
        return;
      }

      // If user is entering a digit and we're at max decimal places, prevent it
      if (/\d/.test(key) && currentValue.includes('.')) {
        const decimalPart = currentValue.split('.')[1];
        if (decimalPart && decimalPart.length >= this.decimalPlaces) {
          const selectionStart = target.selectionStart ?? 0;
          const decimalIndex = currentValue.indexOf('.');
          if (selectionStart > decimalIndex) {
            event.preventDefault();
            return;
          }
        }
      }
    }
    if (this.preventDot && event.key === '.') {
      event.preventDefault();
      return;
    }
    if (this.preventNegative && (event.key === '-' || event.key === '+')) {
      event.preventDefault();
      return;
    }
    this.keypressEvent.emit(event);
  }

  onKeyUp(event: Event): void {
    this.keyupEvent.emit(event);
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  getInputType(): string {
    if (this.inputType === 'password' && this.showPasswordToggle) {
      return this.showPassword ? 'text' : 'password';
    }
    return this.inputType;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.inputType === 'number' && (event.key === 'e' || event.key === 'E')) {
      event.preventDefault();
    }
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;

    // Remove dots on paste if preventDot is true
    if (this.preventDot && value.includes('.')) {
      value = value.replace(/\./g, '');
      target.value = value;
    }

    // Remove negative signs if preventNegative is true
    if (this.preventNegative && value.includes('-')) {
      value = value.replace(/-/g, '');
      target.value = value;
    }

    // Also prevent negative numbers by converting to absolute value
    if (this.preventNegative && this.inputType === 'number') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < 0) {
        value = Math.abs(numValue).toString();
        target.value = value;
      }
    }

    if (this.inputType === 'number' && this.decimalPlaces !== undefined && value.includes('.')) {
      const parts = value.split('.');
      if (parts[1] && parts[1].length > this.decimalPlaces) {
        value = parts[0] + '.' + parts[1].substring(0, this.decimalPlaces);
        target.value = value;
      }
    }

    if (this.inputType === 'number' && /[eE]/.test(value)) {
      value = value.replace(/[eE]/g, '');
      target.value = value;
    }

    if (value.length > this.maxLength) {
      value = value.slice(0, this.maxLength);
      target.value = value;
    }
    if (this.inputType === 'number' && value === '') {
      this.value = '';
      this.onChange('');
      this.inputEvent.emit(event);
      return;
    }

    this.value = value;
    this.onChange(value);
    this.inputEvent.emit(event);
    this.validateRange();
  }

  onBlur(event: Event): void {
    this.onTouched();
    this.validateRange();
    this.customBlur.emit(event);
  }

  private validateRange(): void {
    if (!this.control || !this.value || !this.enableRangeValidation) return;

    const numericValue = parseFloat(this.value);

    if (isNaN(numericValue)) return;

    const min = this.minValue || 0;
    const max = this.maxValue || Infinity;

    const errors = this.control.errors || {};

    delete errors['min'];
    delete errors['max'];
    delete errors['range'];

    if (numericValue < min) {
      errors['min'] = { actualValue: numericValue, minValue: min };
    } else if (numericValue > max) {
      errors['max'] = { actualValue: numericValue, maxValue: max };
    }

    if (Object.keys(errors).length > 0) {
      this.control.setErrors(errors);
    } else {
      this.control.setErrors(null);
    }
  }

  shouldShowError(): boolean {
    if (!this.control) return false;
    return (this.submitted || this.control.touched) && this.control.invalid;
  }

  getErrorMessage(): string {
    if (!this.control || !this.control.errors) return '';

    const errorKey = Object.keys(this.control.errors)[0];

    if (this.errorMessages[errorKey]) {
      return this.errorMessages[errorKey];
    }

    return this.defaultErrorMessages[errorKey] || `${this.label} is invalid`;
  }

  private isControlKey(event: KeyboardEvent): boolean {
    return (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'Tab' ||
      event.key === 'Escape' ||
      event.key === 'Enter' ||
      event.key.startsWith('Arrow') ||
      event.ctrlKey ||
      event.metaKey
    );
  }
}
